/**
 * Canonical event grouping — resolves the many ways one real-world holiday is
 * spelled and slugged across countries down to a single key.
 *
 * Why this exists: `cal_events.event_group` is only ~46% populated and is not
 * canonical (Mawlid alone is stored under `prophet-muhammads-birthday`,
 * `prophet-muhammads-birthday-mawlid`, `prophets-birthday` and `mawlid`), so
 * aggregation keyed on `event_group ?? title` leaves the same holiday scattered
 * across dozens of near-identical rows on the worldwide hubs.
 *
 * `variable: true` marks holidays whose Gregorian date legitimately differs by
 * country (moon sighting, lunisolar reckoning). Only those may be clustered
 * across a date window — fixed-date groups keep exact-date grouping so that,
 * say, Ukraine's Independence Day (Aug 24) never merges into Uruguay's (Aug 25).
 *
 * See: W:\Vibing\Ideation\almanak\docs\platform\prd-worldwide-event-dedup.md
 */

/** Days a `variable` cluster may span from its earliest member. */
export const VARIABLE_DATE_WINDOW_DAYS = 3;

export interface CanonicalGroup {
  /** Canonical key used for grouping. */
  key: string;
  /** Date differs by country (lunar / observational) — may cluster across a date window. */
  variable: boolean;
  /** `event_group` slugs that are aliases of this key. */
  groups?: string[];
  /** Patterns matched against the English `title`. First registry match wins. */
  patterns: RegExp[];
  /** Titles matching these are NOT this group (guards against substring collisions). */
  exclude?: RegExp[];
}

/**
 * Ordered registry — the first entry whose pattern matches wins, so more
 * specific holidays are listed before the broader ones they'd collide with.
 */
export const CANONICAL_GROUPS: CanonicalGroup[] = [
  // --- Islamic (lunar) ---
  {
    key: 'eid-al-fitr',
    variable: true,
    groups: ['eid-al-fitr', 'eid-al-fitr-ramazan-bayram'],
    patterns: [
      /\beid[\s-]?(?:al|ul)?[\s-]?fitr\b/i,
      /end of ramadan/i,
      /hari raya (?:aidil)?fitri/i,
      /idul fitri/i,
      /\blebaran\b/i,
      /ramazan bayram/i,
      /\bkorit[eé]\b/i,
    ],
    // Commercial/travel seasons named after the holiday run for weeks and would
    // stretch a merged cluster's date range far past the holiday itself.
    exclude: [/\bmudik\b|\bmarket\b|\bseason\b/i],
  },
  {
    key: 'eid-al-adha',
    variable: true,
    groups: ['eid-al-adha', 'eid-al-adha-arefa', 'eid-al-adha-kurban-bayram'],
    patterns: [
      /\beid[\s-]?(?:al|ul)?[\s-]?adha\b/i,
      /feast of the sacrifice/i,
      /hari raya (?:aidil)?adha/i,
      /hari raya haji/i,
      /idul adha/i,
      /kurban bayram/i,
      /\btabaski\b/i,
    ],
  },
  {
    key: 'arafat',
    variable: true,
    groups: ['arafat-day-day-of-arafah'],
    patterns: [/\barafat\b/i, /\barafah\b/i],
  },
  {
    key: 'ashura',
    variable: true,
    groups: ['ashura'],
    patterns: [/\bashura\b/i, /\bashoora\b/i],
  },
  {
    key: 'islamic-new-year',
    variable: true,
    groups: ['islamic-new-year'],
    patterns: [/islamic new year/i, /hijri new year/i, /\bmuharram\b/i],
  },
  {
    key: 'isra-miraj',
    variable: true,
    groups: ['isra-miraj'],
    patterns: [/\bisra\b/i, /\bmi'?raj\b/i, /ascension of the prophet/i],
  },
  {
    key: 'mawlid',
    variable: true,
    groups: [
      'prophet-muhammads-birthday',
      'prophet-muhammads-birthday-mawlid',
      'prophets-birthday',
      'mawlid',
    ],
    patterns: [
      /\bmawlid\b/i,
      /\bmaulid\b/i,
      /\bmaulaud\b/i,
      /\bmaouloud\b/i,
      /\bmawloud\b/i,
      /\bmevlid\b/i,
      /\bmilad[\s-]?un[\s-]?nabi\b/i,
      /prophet'?s? birthday/i,
      /prophet muhammad'?s birthday/i,
      /birthday of muhammad/i,
      /youman nabi/i,
      /\bmaulid\b/i,
    ],
    // Mawlid al-Husayn (Cairo) is a different saint's birthday, not the Prophet's.
    exclude: [/husayn|hussein|hussain/i],
  },
  {
    key: 'ramadan-begins',
    variable: true,
    groups: ['ramadan-begins', 'ramadan'],
    patterns: [/first day of ramadan/i, /ramadan begins/i, /start of ramadan/i, /awal ramadan/i],
  },
  {
    key: 'laylat-al-qadr',
    variable: true,
    patterns: [/laylat al[\s-]?qadr/i, /nuzulul? qur/i, /night of power/i],
  },

  // --- Buddhist (lunisolar) ---
  {
    key: 'vesak',
    variable: true,
    groups: ['vesak', 'vesak-buddhas-birthday', 'buddhas-birthday', 'buddhas-birthday-saka-dawa-15th'],
    patterns: [/\bvesak\b/i, /\bwesak\b/i, /visakha bucha/i, /\bwaisak\b/i, /buddha'?s birthday/i, /saga dawa/i],
  },
  {
    key: 'makha-bucha',
    variable: true,
    groups: ['makha-bucha-day'],
    patterns: [/makha bucha/i, /magha puja/i],
  },
  {
    key: 'asalha-bucha',
    variable: true,
    groups: ['asalha-bucha-day', 'asanha-bucha-day'],
    patterns: [/as[ah]lha bucha/i, /asalha puja/i, /dhamma day/i],
  },
  {
    key: 'buddhist-lent-end',
    variable: true,
    patterns: [/end of buddhist lent/i, /ok phansa/i],
  },
  {
    key: 'buddhist-lent-start',
    variable: true,
    groups: ['buddhist-lent-khao-phansa', 'buddhist-lent-day'],
    patterns: [/buddhist lent/i, /khao phansa/i, /\bvassa\b/i],
  },

  // --- Hindu (lunisolar) ---
  { key: 'diwali', variable: true, patterns: [/\bdiwali\b/i, /\bdeepavali\b/i, /\bdipavali\b/i] },
  { key: 'holi', variable: true, patterns: [/\bholi\b/i], exclude: [/holiday/i] },
  { key: 'janmashtami', variable: true, patterns: [/janmashtami/i, /krishna jayanti/i] },
  { key: 'maha-shivaratri', variable: true, patterns: [/shivaratri/i, /shivratri/i] },
  { key: 'raksha-bandhan', variable: true, patterns: [/raksha bandhan/i] },
  { key: 'thaipusam', variable: true, patterns: [/thaipusam/i] },

  // --- Lunar new year (kept apart from the culturally distinct Tet/Seollal/Losar) ---
  {
    key: 'chinese-new-year',
    variable: true,
    groups: ['chinese-new-year', 'chinese-new-year-lunar-new-year', 'lunar-new-year'],
    patterns: [/chinese new year/i, /lunar new year/i, /spring festival/i],
    exclude: [/\btet\b|seollal|losar|tsagaan|hmong|pi mai|ugadi/i],
  },

  // --- Christian movable feasts (Julian/Gregorian split stays apart: >3 days) ---
  { key: 'good-friday', variable: true, groups: ['good-friday'], patterns: [/good friday/i] },
  { key: 'easter-monday', variable: true, groups: ['easter-monday'], patterns: [/easter monday/i] },
  { key: 'easter-sunday', variable: true, patterns: [/easter sunday/i, /easter day/i, /^easter$/i] },
  {
    key: 'ascension-day',
    variable: true,
    groups: ['ascension-day', 'ascension-of-jesus-christ'],
    patterns: [/ascension day/i, /ascension of jesus/i, /ascension of christ/i, /ascension thursday/i],
    exclude: [/prophet|mi'?raj/i],
  },
  { key: 'whit-monday', variable: true, groups: ['whit-monday'], patterns: [/whit monday/i, /pentecost monday/i] },
  { key: 'pentecost', variable: true, patterns: [/\bpentecost\b/i, /whit sunday/i] },
  { key: 'corpus-christi', variable: true, groups: ['corpus-christi'], patterns: [/corpus christi/i] },

  // --- Fixed-date Christian feasts ---
  {
    key: 'christmas',
    variable: false,
    groups: ['christmas', 'orthodox-christmas', 'christmas-eve', 'ethiopian-christmas-genna'],
    patterns: [/christmas/i, /nativity of (?:christ|jesus)/i, /\bgenna\b/i],
    // Academic terms named after the holiday are a different kind of event.
    exclude: [/\bbreak\b|semester|students|school/i],
  },
  { key: 'assumption', variable: false, patterns: [/assumption/i] },
  { key: 'all-saints', variable: false, groups: ['all-saints-day', 'all-saints-day-all-souls-day'], patterns: [/all saints/i, /all souls/i] },
  { key: 'epiphany', variable: false, patterns: [/epiphany/i, /three kings/i, /\btimkat\b/i] },

  // --- Fixed-date civic ---
  {
    key: 'new-years-day',
    variable: false,
    groups: ['new-years-day'],
    patterns: [
      /^(?:the )?new year'?s day$/i,
      /^new year$/i,
      /^(?:day after|second) new year'?s day$/i,
      /^new year'?s day \((?:2nd|second) day\)$/i,
    ],
    // Regional new years (Bengali, Lao, Nowruz, …) fall on their own dates and
    // are their own holidays, not the Jan 1 one.
    exclude: [/islamic|hijri|lunar|chinese|ethiopian|nowruz|bengali|\blao\b|\btet\b|seollal|losar|tsagaan|hmong|pi mai|orthodox|parade/i],
  },
  { key: 'new-years-eve', variable: false, groups: ['new-years-eve'], patterns: [/new year'?s eve/i] },
  {
    key: 'labour-day',
    variable: false,
    groups: ['labour-day', 'international-labour-day'],
    patterns: [/labou?r day/i, /^may day$/i, /international workers/i, /workers'? day/i],
  },

  // Deliberately NOT registered: Independence Day, National Day, Constitution Day,
  // Republic Day, Victory Day, Women's/Children's Day. Countries share those names
  // for entirely different historical events (Adwa Victory Day vs Victory Day 1945;
  // Ukraine's Independence Day vs Uruguay's), so canonicalising them by title would
  // merge unrelated holidays. They keep the existing event_group / title behaviour.
];

const GROUP_ALIAS: Map<string, CanonicalGroup> = (() => {
  const map = new Map<string, CanonicalGroup>();
  for (const group of CANONICAL_GROUPS) {
    for (const slug of group.groups ?? []) {
      if (!map.has(slug)) map.set(slug, group);
    }
  }
  return map;
})();

const BY_KEY: Map<string, CanonicalGroup> = new Map(CANONICAL_GROUPS.map((g) => [g.key, g]));

/** Resolves a title to its canonical group, or null when nothing in the registry matches. */
export function matchCanonicalGroup(title: string): CanonicalGroup | null {
  if (!title) return null;
  for (const group of CANONICAL_GROUPS) {
    if (group.exclude?.some((re) => re.test(title))) continue;
    if (group.patterns.some((re) => re.test(title))) return group;
  }
  return null;
}

/**
 * Country codes an event stands for: the merged list on a deduplicated hub row,
 * otherwise the event's own country.
 */
export function eventCountries(event: { country: string; countries?: string[] }): string[] {
  return event.countries?.length ? event.countries : [event.country];
}

/** Distinct countries represented across a list of (possibly merged) events. */
export function countEventCountries(events: Array<{ country: string; countries?: string[] }>): number {
  const seen = new Set<string>();
  for (const event of events) {
    for (const code of eventCountries(event)) seen.add(code);
  }
  return seen.size;
}

export interface CanonicalKeyInput {
  title: string;
  eventGroup?: string;
}

/**
 * Words that vary between sources naming the same holiday. Dropping them lets
 * Morocco's "Revolution of the King and the People" and Western Sahara's
 * "Anniversary of the Revolution of the King and the People" resolve to one key.
 * Only ever applied together with a date match, so the blast radius is one day.
 */
const TITLE_STOPWORDS = new Set(['the', 'of', 'and', 'a', 'an', 'anniversary']);

/** Fallback key for events the registry does not know: a stopword-stripped title. */
export function normalizeTitleKey(title: string): string {
  const cleaned = title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  const tokens = cleaned.split(' ').filter((t) => t && !TITLE_STOPWORDS.has(t));
  return tokens.length ? tokens.join(' ') : cleaned;
}

/**
 * Grouping key for one event. Registry-backed keys collapse spelling and
 * slug variants; unregistered events fall back to their `event_group`, then to
 * their lowercased title, preserving the previous behaviour for everything the
 * registry does not know about.
 */
export function canonicalEventKey(event: CanonicalKeyInput): string {
  const aliased = event.eventGroup ? GROUP_ALIAS.get(event.eventGroup) : undefined;
  if (aliased) return aliased.key;

  const matched = matchCanonicalGroup(event.title);
  if (matched) return matched.key;

  if (event.eventGroup) return `group:${event.eventGroup}`;
  return `title:${normalizeTitleKey(event.title)}`;
}

/** True when the key's holiday falls on different Gregorian dates by country. */
export function isVariableDateKey(key: string): boolean {
  return BY_KEY.get(key)?.variable ?? false;
}
